<template>
  <v-container class="auth-page py-6" fluid>
    <v-row justify="center">
      <v-col cols="12" sm="9" md="7" lg="6" xl="5">
        <v-card class="auth-card" elevation="2">
          <!-- Header -->
          <div class="auth-header">
            <div class="auth-title-block">
              <div class="auth-title-icon">
                <v-icon color="primary" size="28">
                  mdi-login-variant
                </v-icon>
              </div>

              <div>
                <div class="text-h6 font-weight-bold">
                  Anmelden
                </div>

                <div class="text-body-2 text-medium-emphasis">
                  Melden Sie sich mit Ihrer E-Mail-Adresse und Ihrem Passwort an.
                </div>
              </div>
            </div>
          </div>

          <v-divider />

          <v-card-text class="auth-card-content">
            <v-form
              ref="formRef"
              v-model="valid"
              class="auth-form"
              @submit.prevent="login"
            >
              <div class="form-section">
                <div class="form-section-heading">
                  <v-icon size="22" color="primary">
                    mdi-account-lock-outline
                  </v-icon>

                  <span>Zugangsdaten</span>
                </div>

                <v-text-field
                  v-model.trim="email"
                  type="email"
                  :rules="emailRules"
                  label="E-Mail-Adresse"
                  autocomplete="email"
                  prepend-inner-icon="mdi-email-outline"
                  :disabled="loading"
                  validate-on="blur"
                  class="mb-3"
                  autofocus
                  clearable
                />

                <v-text-field
                  v-model="password"
                  :type="showPassword ? 'text' : 'password'"
                  :rules="passwordRules"
                  label="Passwort"
                  autocomplete="current-password"
                  prepend-inner-icon="mdi-lock-outline"
                  :append-inner-icon="
                    showPassword ? 'mdi-eye-off' : 'mdi-eye'
                  "
                  :disabled="loading"
                  validate-on="blur"
                  class="mb-1"
                  @click:append-inner="showPassword = !showPassword"
                />
              </div>

              <!-- <div class="login-options">
                <v-btn
                  variant="text"
                  color="primary"
                  size="small"
                  :disabled="loading || !email"
                  @click="showForgotPasswordDialog = true"
                >
                  <v-icon start>mdi-help-circle-outline</v-icon>
                  Passwort vergessen?
                </v-btn>
              </div> -->

              <div v-if="attemptedLogin" class="login-options">
                <v-btn
                  variant="text"
                  color="primary"
                  size="small"
                  :disabled="loading"
                  @click="showForgotPasswordDialog = true"
                >
                  <v-icon start>mdi-help-circle-outline</v-icon>
                  Passwort vergessen?
                </v-btn>
              </div>
            </v-form>
          </v-card-text>

          <v-divider />

          <!-- Footer -->
          <div class="auth-footer">
            <v-btn
              variant="text"
              :disabled="loading"
              @click="goToRegister"
            >
              Noch nicht registriert?
            </v-btn>

            <v-btn
              color="primary"
              variant="elevated"
              :loading="loading"
              :disabled="!valid || loading"
              class="submit-button"
              @click="login"
            >
              <v-icon start>mdi-login</v-icon>
              Anmelden
            </v-btn>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <!-- Forgot-password confirmation -->
    <v-dialog
      v-model="showForgotPasswordDialog"
      max-width="480"
      persistent
    >
      <v-card class="auth-dialog">
        <div class="dialog-header">
          <div class="dialog-icon">
            <v-icon color="primary" size="26">
              mdi-lock-question
            </v-icon>
          </div>

          <div>
            <div class="text-h6 font-weight-bold">
              Passwort vergessen
            </div>

            <div class="text-body-2 text-medium-emphasis">
              Anfrage zum Zurücksetzen senden
            </div>
          </div>
        </div>

        <v-divider />

        <v-card-text class="dialog-content">
          <p class="mb-3">
            Möchten Sie Ihr Passwort für folgende E-Mail-Adresse
            zurücksetzen lassen?
          </p>

          <div class="email-summary">
            <v-icon color="primary">mdi-email-outline</v-icon>
            <span>{{ email || 'Keine E-Mail-Adresse angegeben' }}</span>
          </div>

          <v-alert
            v-if="!email || !emailMatch(email)"
            type="warning"
            variant="tonal"
            border="start"
            class="mt-4 dialog-alert"
          >
            Bitte geben Sie zuerst eine gültige E-Mail-Adresse ein.
          </v-alert>
        </v-card-text>

        <v-divider />

        <v-card-actions class="dialog-actions">
          <v-btn
            variant="text"
            :disabled="forgotPasswordLoading"
            @click="showForgotPasswordDialog = false"
          >
            Abbrechen
          </v-btn>

          <v-btn
            color="primary"
            variant="elevated"
            :loading="forgotPasswordLoading"
            :disabled="!email || !emailMatch(email)"
            @click="proceedWithForgotPassword"
          >
            <v-icon start>mdi-send-outline</v-icon>
            Anfrage senden
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Forgot-password success -->
    <v-dialog
      v-model="showSuccessDialog"
      max-width="520"
      persistent
    >
      <v-card class="auth-dialog">
        <div class="dialog-header">
          <div class="dialog-icon success-icon">
            <v-icon color="success" size="26">
              mdi-check-circle-outline
            </v-icon>
          </div>

          <div>
            <div class="text-h6 font-weight-bold">
              Anfrage erfolgreich gesendet
            </div>

            <div class="text-body-2 text-medium-emphasis">
              Ihr Passwort wird zurückgesetzt
            </div>
          </div>
        </div>

        <v-divider />

        <v-card-text class="dialog-content">
          <p>
            Sie erhalten demnächst ein Einmalpasswort.
          </p>

          <p class="mt-3 mb-0">
            Um den Vorgang zu beschleunigen, können Sie zusätzlich eine
            E-Mail an
            <a 
              href="mailto:earns@rom-technik.de"
            >
            earns@rom-technik.de
            </a>
            senden.
          </p>
        </v-card-text>

        <v-divider />

        <v-card-actions class="dialog-actions">
          <v-spacer />

          <v-btn
            color="primary"
            variant="elevated"
            @click="proceedToLogin"
          >
            Verstanden
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script setup>
import { ref } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { toast } from 'vue3-toastify'
import api from '@/plugins/axios'
import { emailMatch } from '@/utils.js'
import { useProjectStore } from '@/stores/projectStore'

const router = useRouter()
const route = useRoute()
const store = useProjectStore()

const formRef = ref(null)

const email = ref('')
const password = ref('')

const attemptedLogin = ref(false)

const valid = ref(false)
const loading = ref(false)
const forgotPasswordLoading = ref(false)

const showPassword = ref(false)
const showForgotPasswordDialog = ref(false)
const showSuccessDialog = ref(false)

const emailRules = [
  value =>
    (!!value && emailMatch(value)) ||
    'Bitte geben Sie eine gültige E-Mail-Adresse ein.',
]

const passwordRules = [
  value =>
    !!value ||
    'Bitte geben Sie ein Passwort ein.',
]

function goToRegister() {
  if (router.hasRoute('Register')) {
    router.push({ name: 'Register' })
  }
}

// async function login() {
//   if (loading.value) return

//   const result = await formRef.value?.validate?.()

//   if (!result?.valid) {
//     toast.warning('Bitte überprüfen Sie Ihre Zugangsdaten.')
//     return
//   }

//   loading.value = true

//   try {
//     await store.login({
//       email: email.value,
//       password: password.value,
//     })

//     if (store.forgotPassword) {
//       router.replace({ name: 'ForgotPassword' })
//       return
//     }

//     toast.success('Erfolgreich angemeldet.')

//     const redirect =
//       typeof route.query.redirect === 'string'
//         ? route.query.redirect
//         : '/'

//     router.replace(redirect)
//   } catch (error) {
//     const data = error?.response?.data

//     if (data?.inactive_user) {
//       const messages = Array.isArray(data.inactive_user)
//         ? data.inactive_user
//         : [String(data.inactive_user)]

//       toast.warning(messages.join(' '))
//     } else if (data?.invalid_password) {
//       const messages = Array.isArray(data.invalid_password)
//         ? data.invalid_password
//         : [String(data.invalid_password)]

//       toast.error(messages.join(' '))
//     } else {
//       toast.error('Falsche E-Mail-Adresse oder falsches Passwort.')
//     }
//   } finally {
//     loading.value = false
//   }
// }

async function login() {
  if (loading.value) return

  const result = await formRef.value?.validate?.()

  if (!result?.valid) {
    toast.warning('Bitte überprüfen Sie Ihre Zugangsdaten.')
    return
  }

  loading.value = true
  attemptedLogin.value = false

  try {
    await store.login({
      email: email.value,
      password: password.value,
    })

    if (store.forgotPassword) {
      router.replace({ name: 'ForgotPassword' })
      return
    }

    toast.success('Erfolgreich angemeldet.')

    const requestedRedirect =
      typeof route.query.redirect === 'string'
        ? route.query.redirect
        : null

    // Avoid redirecting back into the login flow (redirect loop).
    const redirect =
      requestedRedirect && !requestedRedirect.startsWith('/login')
        ? requestedRedirect
        : '/'

    router.replace(redirect)
  } catch (error) {
    const data = error?.response?.data

    if (data?.inactive_user) {
      attemptedLogin.value = false

      const messages = Array.isArray(data.inactive_user)
        ? data.inactive_user
        : [String(data.inactive_user)]

      toast.warning(messages.join(' '))
    } else if (data?.invalid_password) {
      /*
       * The backend recognized the account but rejected
       * the password. Now display "Passwort vergessen?".
       */
      attemptedLogin.value = true

      const messages = Array.isArray(data.invalid_password)
        ? data.invalid_password
        : [String(data.invalid_password)]

      toast.error(messages.join(' '))
    } else {
      attemptedLogin.value = false
      toast.error('Falsche E-Mail-Adresse oder falsches Passwort.')
    }
  } finally {
    loading.value = false
  }
}

async function proceedWithForgotPassword() {
  if (
    forgotPasswordLoading.value ||
    !email.value ||
    !emailMatch(email.value)
  ) {
    return
  }

  forgotPasswordLoading.value = true

  try {
    await api.post('/auth/login/forgot-password/', {
      email: email.value,
    })

    showForgotPasswordDialog.value = false
    showSuccessDialog.value = true

    toast.success(
      'Die Anfrage zum Zurücksetzen des Passworts wurde gesendet.'
    )
  } catch {
    toast.error(
      'Die Anfrage zum Zurücksetzen des Passworts konnte nicht gesendet werden.'
    )
  } finally {
    forgotPasswordLoading.value = false
  }
}

function proceedToLogin() {
  showSuccessDialog.value = false
}
</script>

<style scoped lang="scss">
.auth-page {
  min-height: 100%;
}

.auth-card {
  overflow: hidden;

  border: 1px solid rgba(var(--v-theme-on-surface), 0.08);
  border-radius: 28px;

  color: rgb(var(--v-theme-on-surface));
  background-color: rgb(var(--v-theme-surface));
}

/* Header */
.auth-header {
  padding: 1.25rem 1.5rem;

  background: linear-gradient(
    180deg,
    rgba(var(--v-theme-primary), 0.07),
    rgba(var(--v-theme-primary), 0.025)
  );
}

.auth-title-block {
  display: flex;
  align-items: center;
  gap: 0.9rem;
}

.auth-title-icon {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;

  width: 48px;
  height: 48px;
  border-radius: 999px;

  background-color: rgba(var(--v-theme-primary), 0.08);
}

/* Main content */
.auth-card-content {
  display: grid;
  gap: 1.25rem;
  padding: 1.5rem;
}

.registration-hint {
  border-radius: 20px;
}

/* Form */
.auth-form {
  display: grid;
  gap: 1.25rem;
}

.form-section {
  padding: 1rem;

  border: 1px solid rgba(var(--v-theme-on-surface), 0.07);
  border-radius: 24px;

  background-color: rgba(var(--v-theme-primary), 0.025);
}

.form-section-heading {
  display: flex;
  align-items: center;
  gap: 0.55rem;

  margin-bottom: 1rem;
  padding: 0.75rem 0.9rem;

  border: 1px solid rgba(var(--v-theme-primary), 0.1);
  border-radius: 18px;

  color: rgb(var(--v-theme-primary));
  background-color: rgba(var(--v-theme-primary), 0.055);

  font-size: 1rem;
  font-weight: 700;
}

.auth-form :deep(.v-field) {
  border-radius: 18px;
}

.login-options {
  display: flex;
  justify-content: flex-end;
}

/* Footer */
.auth-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;

  padding: 1rem 1.5rem;
}

.auth-footer .v-btn {
  font-weight: 600;
  text-transform: none;
}

.submit-button {
  min-width: 150px;
}

/* Dialogs */
.auth-dialog {
  overflow: hidden;
  border-radius: 26px;
}

.dialog-header {
  display: flex;
  align-items: center;
  gap: 0.85rem;

  padding: 1.25rem;

  background: linear-gradient(
    180deg,
    rgba(var(--v-theme-primary), 0.07),
    rgba(var(--v-theme-primary), 0.025)
  );
}

.dialog-icon {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;

  width: 46px;
  height: 46px;
  border-radius: 999px;

  background-color: rgba(var(--v-theme-primary), 0.08);
}

.success-icon {
  background-color: rgba(var(--v-theme-success), 0.1);
}

.dialog-content {
  padding: 1.25rem;
  line-height: 1.55;
}

.dialog-actions {
  padding: 1rem 1.25rem;
}

.dialog-actions .v-btn {
  font-weight: 600;
  text-transform: none;
}

.dialog-alert {
  border-radius: 18px;
}

.email-summary {
  display: flex;
  align-items: center;
  gap: 0.65rem;

  padding: 0.8rem 0.9rem;

  border: 1px solid rgba(var(--v-theme-primary), 0.1);
  border-radius: 18px;

  background-color: rgba(var(--v-theme-primary), 0.04);

  font-weight: 600;
  word-break: break-word;
}

.support-link {
  color: rgb(var(--v-theme-primary));
  font-weight: 600;
  text-decoration: none;
}

.support-link:hover {
  text-decoration: underline;
}

/* Responsive */
@media (max-width: 700px) {
  .auth-page {
    padding-inline: 0.5rem;
  }

  .auth-header {
    padding: 1rem;
  }

  .auth-title-block {
    align-items: flex-start;
  }

  .auth-title-icon {
    width: 42px;
    height: 42px;
  }

  .auth-card-content {
    padding: 1rem;
  }

  .form-section {
    padding: 0.8rem;
    border-radius: 20px;
  }

  .auth-footer {
    align-items: stretch;
    flex-direction: column-reverse;
    padding: 1rem;
  }

  .auth-footer .v-btn {
    width: 100%;
  }

  .submit-button {
    min-width: 0;
  }

  .login-options {
    justify-content: center;
  }

  .dialog-header {
    align-items: flex-start;
    padding: 1rem;
  }

  .dialog-content {
    padding: 1rem;
  }

  .dialog-actions {
    align-items: stretch;
    flex-direction: column-reverse;
  }

  .dialog-actions .v-btn {
    width: 100%;
  }
}
</style>